import React from 'react'
import duoPersonIcon from '@assets/svg/duo-person-icon.svg'
import feedbackIcon from '@assets/svg/feedback-icon.svg'
import homeIcon from '@assets/svg/home-icon.svg'
import personIcon from '@assets/svg/person-uno.svg'
import rulerIcon from '@assets/svg/ruler-icon.svg'
import { logout } from '@features/auth/auth-actions'
import { useUser } from '@features/auth/hooks'
import { useTheme } from '@features/theme/theme.hooks'
import { modeType, setThemeMode } from '@features/theme/theme.slice'
import { useToast } from '@features/toast/hooks'
import { AnimatePresence, motion, useAnimation } from 'motion/react'
import { usePaymentGetCreditsQuery } from '@app/services/auto-generated/payment'
import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@common/components'
import { languages } from '@common/constants/languages'
import {
  BIO_ROUTE,
  COMMUNITY_ROUTE,
  DASHBOARD_ROUTE,
  FEEDBACK_ROUTE,
  LOGIN_ROUTE,
  MAIN_ROUTE,
  MY_CLASSROOMS_ROUTE,
  PERSONAL_ROUTE,
  PRICING_ROUTE,
  QUIZ_VOCAB_ROUTE,
} from '@common/constants/routes'
import {
  useAppDispatch,
  useAppSelector,
  useMediaQuery,
  useToggle,
  useTranslation,
} from '@common/hooks'
import useToggleMenu from '@common/hooks/use-toggle-menu'
import {
  DarkModeIcon,
  LanguageIcon,
  LogoutIcon,
  MenuIcon,
  PersonIcon,
  WbSunnyIcon,
} from '@common/icons'
import { handleGravatar } from '@common/utils/gravatar'
import { RouterLink, useLocation, useNavigate } from '@lib/router'
import { creditsSystem } from '@config/features-flags'
import { Language } from '@types'
import { isUNSW } from '../../../../utils/unswUtils'
import { NavLinkButton, navlinks } from '../header'
import { ExpandableMenuItem, UserOverviewContainer } from './profile-menu.styles'

interface ProfileMenuProps {
  className?: string
}

const ProfileMenu: React.FC<ProfileMenuProps> = (props) => {
  const { t, i18n } = useTranslation('common')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const matches = useMediaQuery(990)
  const { user } = useUser()
  const { isDark } = useTheme()
  const { pathname } = useLocation()
  const selectedTheme = useAppSelector((state) => state.theme)
  const { anchorEl, open, handleClick, handleClose: handleCloseMenu } = useToggleMenu()
  const [selectedLanguage, setSelectedLanguage] = React.useState<Language>(languages[0])
  const {
    open: openLanguageMenu,
    handleToggle: handleToggleLanguageMenu,
    handleClose: handleCloseLanguageMenu,
  } = useToggle(false)
  const {
    open: openThemeMenu,
    handleToggle: handleToggleThemeMenu,
    handleClose: handleCloseThemeMenu,
  } = useToggle(false)
  const handleClose = () => {
    handleCloseMenu()
    handleCloseLanguageMenu()
    handleCloseThemeMenu()
  }
  const { data } = usePaymentGetCreditsQuery(undefined, { skip: !creditsSystem })

  const hanldeChangeLanguage = (code: string) => {
    i18n.changeLanguage(code)
    const foundLanguage = languages.find((lang) => lang.code === code)
    if (foundLanguage) {
      setSelectedLanguage(foundLanguage)
    }
  }

  const handleChangeThemeMode = (mode: modeType) => {
    dispatch(setThemeMode(mode))
  }

  const handleLogoutClick = () => {
    dispatch(logout())
    handleClose()
    navigate(isUNSW() ? LOGIN_ROUTE : MAIN_ROUTE)
  }

  const toast = useToast()
  const handleToast = () => {
    toast('This feature is not available at the moment', 'warning')
  }

  const themeOptions = [
    {
      value: 'light' as modeType,
      label: t('header.menu.lightTheme', 'Light theme'),
    },
    {
      value: 'dark' as modeType,
      label: t('header.menu.darkTheme', 'Dark theme'),
    },
    {
      value: 'browser' as modeType,
      label: t('header.menu.deviceTheme', 'Device theme'),
    },
  ]

  return (
    <>
      <IconButton color='neutral' onClick={handleClick}>
        {matches ? (
          <MenuIcon className='custom-menu-icon' />
        ) : (
          <PersonIcon className='custom-menu-icon' />
        )}
      </IconButton>
      <Menu
        elevation={1}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{ zIndex: 999999 }}
        {...props}
      >
        <UserOverview
          username={user?.username || ''}
          avatarUrl={handleGravatar(user?.image || '')}
          handleClose={handleClose}
        />
        <Divider />
        {matches && (
          <Box>
            {creditsSystem && (
              <Button
                sx={{ display: 'flex', mx: 'auto', mt: 1, width: '120px' }}
                variant='outlined'
                component={RouterLink}
                to={PRICING_ROUTE}
              >
                {data ? data.credits : '-'} {t('header.credits', 'Credits')}
              </Button>
            )}
            <Stack spacing={1} sx={{ mx: 'auto', my: 1, width: '80%' }}>
              <NavLinkButton
                link={MY_CLASSROOMS_ROUTE}
                isActive={pathname.startsWith(MY_CLASSROOMS_ROUTE)}
                bgColor='#FFE9B0'
                icon={duoPersonIcon}
              >
                Live Workshop
              </NavLinkButton>
              <NavLinkButton
                link={DASHBOARD_ROUTE}
                isActive={
                  pathname.startsWith('/dashboard') &&
                  !pathname.startsWith(FEEDBACK_ROUTE) &&
                  !pathname.startsWith(QUIZ_VOCAB_ROUTE) &&
                  !pathname.startsWith(COMMUNITY_ROUTE)
                }
                bgColor='#D1CAFF'
                width={15}
                icon={personIcon}
              >
                AI Interview
              </NavLinkButton>
              <NavLinkButton
                link={COMMUNITY_ROUTE}
                isActive={pathname.startsWith(COMMUNITY_ROUTE)}
                bgColor='#A8CAFF'
                width={15}
                icon={homeIcon}
              >
                Community
              </NavLinkButton>
              <NavLinkButton
                link={FEEDBACK_ROUTE}
                isActive={pathname.startsWith(FEEDBACK_ROUTE)}
                bgColor='#FFCCC9'
                width={15}
                icon={feedbackIcon}
              >
                AI Feedback
              </NavLinkButton>
              <NavLinkButton
                link={QUIZ_VOCAB_ROUTE}
                isActive={pathname.startsWith(QUIZ_VOCAB_ROUTE)}
                bgColor='#A7E4B7'
                width={15}
                icon={rulerIcon}
              >
                Exercises
              </NavLinkButton>
            </Stack>
            {navlinks.map((link, k) => (
              <MenuItem
                component={RouterLink}
                to={link.to}
                onClick={() => {
                  handleClose()
                  if (link?.to === '#') {
                    handleToast()
                  }
                }}
                key={k}
              >
                {link.label}
              </MenuItem>
            ))}
            <Divider />
          </Box>
        )}
        {!isUNSW() && (
          <MenuItem onClick={handleToggleThemeMenu}>
            <ListItemIcon>
              <AnimatePresence>
                {isDark ? (
                  <DarkModeIcon
                    fontSize='small'
                    component={motion.svg}
                    initial={{ x: -5, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ opacity: 0.5, x: 5 }}
                  />
                ) : (
                  <WbSunnyIcon
                    fontSize='small'
                    component={motion.svg}
                    initial={{ x: -5, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ opacity: 0.5, x: 5 }}
                  />
                )}
              </AnimatePresence>
            </ListItemIcon>
            {t('header.menu.language.Theme', 'Theme')}
          </MenuItem>
        )}
        <ExpandableMenu
          options={themeOptions}
          selectedValue={selectedTheme.mode}
          open={openThemeMenu}
          getOptionValue={(mode) => mode.value}
          getOptionLabel={(mode) => mode.label}
          onOptionClick={(option) => handleChangeThemeMode(option.value as modeType)}
        />
        <MenuItem onClick={handleToggleLanguageMenu}>
          <ListItemIcon>
            <LanguageIcon fontSize='small' />
          </ListItemIcon>
          {t('header.menu.language.label', 'Language')}
        </MenuItem>
        <ExpandableMenu
          options={languages.map((lang) => ({
            value: lang.code,
            label: lang.name,
            flag_img: lang.flag_img,
          }))}
          selectedValue={selectedLanguage.code}
          open={openLanguageMenu}
          getOptionValue={(lng) => lng.value}
          getOptionLabel={(lng) => lng.label}
          onOptionClick={(option) => hanldeChangeLanguage(option.value as string)}
        />
        <MenuItem onClick={handleLogoutClick}>
          <ListItemIcon>
            <LogoutIcon fontSize='small' />
          </ListItemIcon>
          {t('header.menu.logout.label', 'Logout')}
        </MenuItem>
      </Menu>
    </>
  )
}

interface UserOverviewProps {
  username: string
  avatarUrl?: string
  handleClose: () => void
}

const UserOverview = (props: UserOverviewProps) => {
  const { username, avatarUrl, handleClose } = props
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const { isTeacher } = useUser()
  return (
    <UserOverviewContainer>
      <Stack direction='row' spacing={1} alignItems='center'>
        <Avatar src={avatarUrl} />
        <Stack>
          <Typography variant='body1' fontWeight={600}>
            {username}
          </Typography>
          {pathname.startsWith('/classroom/') ? (
            <Link
              href={isTeacher && !isUNSW() ? BIO_ROUTE : PERSONAL_ROUTE}
              target='_blank'
              rel='noopener'
              underline='hover'
              color='secondary'
              variant='body2'
              onClick={handleClose}
            >
              {t('header.menu.manageAccount', 'Manage your account')}
            </Link>
          ) : (
            <Link
              underline='hover'
              color='secondary'
              variant='body2'
              component={RouterLink}
              to={isTeacher && !isUNSW() ? BIO_ROUTE : PERSONAL_ROUTE}
              onClick={handleClose}
            >
              {t('header.menu.manageAccount', 'Manage your account')}
            </Link>
          )}
        </Stack>
      </Stack>
    </UserOverviewContainer>
  )
}

interface ExpandableMenuProps {
  open: boolean
  options: Array<{
    value: string | number
    label: string
    flag_img?: string
  }>
  selectedValue: string | number
  getOptionLabel: (option: { value: string | number; label: string; flag_img?: string }) => string
  getOptionValue: (option: {
    value: string | number
    label: string
    flag_img?: string
  }) => string | number
  onOptionClick: (option: { value: string | number; label: string; flag_img?: string }) => void
}

const ExpandableMenu = (props: ExpandableMenuProps) => {
  const {
    open,
    options,
    selectedValue,
    getOptionLabel = (option) => option.label,
    getOptionValue = (option) => option.value,
    onOptionClick,
  } = props
  const controls = useAnimation()

  React.useEffect(() => {
    if (open) {
      controls.start('animate')
    } else {
      controls.start('hidden')
    }
  }, [open])

  return (
    <Box
      component={motion.div}
      variants={{
        hidden: { height: 0 },
        animate: { height: 'unset' },
      }}
      animate={controls}
      sx={{
        py: 0,
        pb: 0,
        maxHeight: '200px',
        height: 0,
        overflow: options.length > 4 ? 'auto' : 'hidden',
      }}
    >
      {options.map((option, k) => {
        const selected = getOptionValue(option) === selectedValue
        return (
          <ExpandableMenuItem
            key={k}
            selected={selected}
            isLanguage={option.flag_img !== undefined}
            onClick={() => onOptionClick(option)}
          >
            {getOptionLabel(option)}
            {option.flag_img && <img src={option.flag_img} alt='flag' style={{ width: '25px' }} />}
          </ExpandableMenuItem>
        )
      })}
    </Box>
  )
}

export default ProfileMenu
